// src/features/portfolio-optimization-tools/my-strategies/components/TacticalTradeLedger.tsx
//
// The Robo-Advisor "Tactical Operations Center" — a Bloomberg/Aladdin-style
// trade blotter that visualises exactly how the AI defends alpha by buying and
// selling. It renders the pending Buy/Sell ledger generated by the most recent
// tactical rebalance and exposes a "Force AI Rebalance" trigger.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { baseAsset } from '../../../crypto-ranking/api';
import type { TradeInstructionRow } from '../../multi-asset-portfolio-optimizer/api';
import {
    ActivityIcon,
    TargetIcon,
} from '../../multi-asset-portfolio-optimizer/components/icons';

// Maps each backend sleeve id onto its single-symbol deep-dive info page, so a
// trade's ticker deep-links straight to that instrument's detail view. Mirrors
// SYMBOL_DEEPDIVE_ROUTES in QuantXRayTable, plus commodities.
const SYMBOL_DEEPDIVE_ROUTES: Record<string, string> = {
    equities: '/dashboard/stock-info-page',
    etf: '/dashboard/etf-info-page',
    reits: '/dashboard/reit-info-page',
    cryptocurrencies: '/dashboard/crypto-info-page',
    commodities: '/dashboard/commodity-info-page',
    fx: '/dashboard/fx-info-page',
};

// The crypto sleeve keys coins by their FMP USD pair (e.g. "BTCUSD"), but the
// crypto-info deep-dive page + its description JSON use the bare base ticker
// (e.g. "BTC"). Every other sleeve deep-links with its symbol verbatim.
const deepDiveSymbol = (assetClass: string, symbol: string): string =>
    assetClass === 'cryptocurrencies' ? baseAsset(symbol) : symbol;

interface Props {
    trades: TradeInstructionRow[];
    isRebalancing: boolean;
    onExecuteRebalance: () => void;
    regime: string;
    totalNav: number;
    /** True when the strategy has already rebalanced today — the manual
     * "Force AI Rebalance" is locked out until tomorrow (idempotency guard). */
    isLockedForToday?: boolean;
    /** UTC ISO timestamp of the most recent tactical rebalance, if any. */
    lastRebalancedAt?: string | null;
}

const formatCurrency = (val: number): string =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(val);

export const TacticalTradeLedger = ({
    trades,
    isRebalancing,
    onExecuteRebalance,
    regime,
    totalNav,
    isLockedForToday = false,
    lastRebalancedAt = null,
}: Props) => {
    const buys = trades.filter((t) => t.action === 'BUY').length;
    const sells = trades.filter((t) => t.action === 'SELL').length;

    // Disable the manual trigger while a rebalance is in flight OR once today's
    // rebalance has already fired (the backend idempotency guard would reject a
    // second click with "already_rebalanced_today" anyway).
    const isButtonDisabled = isRebalancing || isLockedForToday;
    const lastRebalancedLabel = lastRebalancedAt
        ? new Date(lastRebalancedAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : null;

    // Order the blotter by Target Shift from maximum to minimum: the largest
    // conviction buys sit up top, the deepest sells at the bottom.
    const sortedTrades = useMemo(
        () => [...trades].sort((a, b) => b.weight_delta - a.weight_delta),
        [trades],
    );

    return (
        <div className="surface-card !p-0 overflow-hidden border border-amber-500/30 shadow-[0_0_30px_rgba(245,158,11,0.08)] relative mb-8">
            {/* Glowing amber background effect. */}
            <div className="absolute -top-32 -right-32 w-64 h-64 bg-amber-500/10 blur-3xl rounded-full pointer-events-none" />

            <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-10">
                <div>
                    <h3 className="section-title text-xl flex items-center gap-2 mb-1 text-amber-600 dark:text-amber-400">
                        <ActivityIcon className="w-5 h-5" />
                        Robo-Advisor: Tactical Operations Center
                    </h3>
                    <p className="muted text-xs max-w-2xl">
                        Autonomous tactical shifting to defend capital and lock
                        in positive excess alpha. Current Regime:{' '}
                        <span className="text-slate-800 dark:text-white font-bold uppercase tracking-wider">
                            {regime}
                        </span>
                        . Sells are executed first to raise cash.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block border-r border-slate-300 dark:border-slate-700 pr-4">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-0.5">
                            Live NAV
                        </p>
                        <p className="text-xl font-mono font-black text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(totalNav)}
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <button
                            type="button"
                            onClick={onExecuteRebalance}
                            disabled={isButtonDisabled}
                            title={
                                isLockedForToday
                                    ? 'Already rebalanced today — Forge Ultra runs at most once per day per strategy.'
                                    : undefined
                            }
                            className="bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-bold text-sm px-4 py-2.5 rounded-lg shadow-lg transition-all flex items-center gap-2 uppercase tracking-wider whitespace-nowrap"
                        >
                            {isRebalancing ? (
                                <span className="animate-pulse">
                                    Computing Matrix…
                                </span>
                            ) : isLockedForToday ? (
                                <>
                                    <TargetIcon className="w-4 h-4" /> Rebalanced
                                    Today
                                </>
                            ) : (
                                <>
                                    <TargetIcon className="w-4 h-4" /> Force Forge
                                    Ultra Rebalance
                                </>
                            )}
                        </button>
                        {lastRebalancedLabel && (
                            <p className="text-[10px] text-slate-500 font-medium">
                                Last run: {lastRebalancedLabel}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 grid grid-cols-3 gap-4 border-b border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-900/40 relative z-10">
                <div className="text-center">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                        Total Signals
                    </p>
                    <p className="text-xl font-mono text-slate-800 dark:text-slate-200">
                        {trades.length}
                    </p>
                </div>
                <div className="text-center border-l border-slate-200 dark:border-slate-800">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                        Buy Orders
                    </p>
                    <p className="text-xl font-mono text-emerald-600 dark:text-emerald-400">
                        {buys}
                    </p>
                </div>
                <div className="text-center border-l border-slate-200 dark:border-slate-800">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                        Sell Orders
                    </p>
                    <p className="text-xl font-mono text-red-600 dark:text-red-400">
                        {sells}
                    </p>
                </div>
            </div>

            <div className="overflow-x-auto relative z-10">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-950/50 text-[10px] uppercase font-bold text-slate-500">
                        <tr>
                            <th className="px-6 py-4">Action</th>
                            <th className="px-6 py-4">Asset Ticker</th>
                            <th className="px-6 py-4">Asset Class</th>
                            <th className="px-6 py-4 text-right">Target Shift</th>
                            <th className="px-6 py-4 text-right">
                                Est. Trade Value
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800/50 font-mono bg-white dark:bg-transparent">
                        {trades.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="py-10 text-center text-slate-500 text-sm"
                                >
                                    No tactical rebalancing required today. The
                                    portfolio is mathematically optimal.
                                </td>
                            </tr>
                        ) : (
                            sortedTrades.map((trade, i) => {
                                const isBuy = trade.action === 'BUY';
                                const isSell = trade.action === 'SELL';
                                const route =
                                    SYMBOL_DEEPDIVE_ROUTES[trade.asset_class];
                                const infoHref = route
                                    ? `${route}?symbol=${encodeURIComponent(
                                          deepDiveSymbol(
                                              trade.asset_class,
                                              trade.symbol,
                                          ),
                                      )}`
                                    : null;
                                return (
                                    <tr
                                        key={`${trade.symbol}-${i}`}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                                    >
                                        <td className="px-6 py-3">
                                            <span
                                                className={`px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest ${
                                                    isBuy
                                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border border-emerald-500/20'
                                                        : isSell
                                                          ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400 border border-red-500/20'
                                                          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 border border-slate-700/50'
                                                }`}
                                            >
                                                {trade.action}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 font-bold">
                                            {infoHref ? (
                                                <Link
                                                    to={infoHref}
                                                    title={`View ${trade.symbol} deep-dive`}
                                                    className="text-amber-600 dark:text-amber-400 hover:underline"
                                                >
                                                    {trade.symbol}
                                                </Link>
                                            ) : (
                                                <span className="text-slate-900 dark:text-white">
                                                    {trade.symbol}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-slate-500 text-[11px] font-sans uppercase tracking-wider font-semibold">
                                            {trade.asset_class}
                                        </td>
                                        <td
                                            className={`px-6 py-3 text-right font-bold ${
                                                isBuy
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : isSell
                                                      ? 'text-red-600 dark:text-red-400'
                                                      : 'text-slate-500'
                                            }`}
                                        >
                                            {isBuy ? '+' : ''}
                                            {(trade.weight_delta * 100).toFixed(
                                                2,
                                            )}
                                            %
                                        </td>
                                        <td className="px-6 py-3 text-right text-slate-600 dark:text-slate-300 font-semibold">
                                            {formatCurrency(
                                                trade.net_trade_value,
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TacticalTradeLedger;
